/**
 * Effect DSL interpreter. Placement effects (telegraph, zone, summon, dash, modifyCooldown,
 * conditional) run once per invocation; every other effect runs per resolved target
 * (`to`: target | self | area via sys.spatial), gated by its `if` condition.
 */
import type {
  AbsorbEffect,
  DashEffect,
  DispelEffect,
  EffectCtx,
  EffectSpec,
  GripEffect,
  KnockbackEffect,
  SimContext,
  SummonEffect,
  TelegraphEffect,
  Unit,
  UnitId,
  Vec2,
  Zone,
  ZoneEffect,
} from '../contracts';
import { MELEE_RANGE_BONUS, MIN_MELEE_RANGE, TICK_MS } from '../contracts';
import { computeAmount, computeAmountEx } from './amount';
import { applyAuraDef, fireProcs, removeAura, removeInstance, removeStacks } from './auras';
import { queueEchoes } from './clones'; // +rogue-specs
import { spawnPets } from './pets'; // +acolyte-specs (S7)
import { companionCast, companionOf, spawnCompanion } from './pets'; // +ranger-specs (S7 companions)
import { evalCondition } from './conditions';
import { dealDamage, heal } from './damage';
import { changeResource } from './resources';
import { critChanceFor, damageDoneMult, healingDoneMult, unitEffectiveStats } from './stats';
import { addHealingThreat, addThreat, scaleThreat, taunt } from './threat';
import { affectsMatch, dirBetween, GRIP_SPEED, isHostile, MAX_EFFECT_DEPTH, unitOf } from './util';

type OnceType = 'telegraph' | 'zone' | 'summon' | 'dash' | 'modifyCooldown' | 'conditional' | 'mechanic' | 'companionCast'; // +encounters: mechanic; +ranger-specs: companionCast
type PerTargetEffect = Exclude<EffectSpec, { type: OnceType }>;

/** Effects whose missing primary target falls back to the caster. */
function selfFallback(e: PerTargetEffect): boolean {
  return e.type === 'heal' || e.type === 'absorb' || e.type === 'resource' || (e.type === 'applyAura' && e.aura.kind === 'buff');
}

/**
 * Public entry (CombatSystem.applyEffects). When `ectx.isCast` is set (casting.executeAbility,
 * channel start) this is a completed cast: 'cast' procs fire before the effects and
 * 'castResolved' procs (F3) after they resolved on every target. Flat threat is added by casting.
 */
export function applyEffects(ctx: SimContext, effects: readonly EffectSpec[], ectx: EffectCtx): void {
  if (!ectx.isCast) {
    runEffects(ctx, effects, ectx);
    return;
  }
  const castStartedAt = ectx.castStartedAt ?? ctx.now;
  const castStartSeq = ectx.castStartSeq ?? (ectx.castStartedAt === undefined || ectx.castStartedAt === ctx.now ? ctx.world.nextSeq : undefined);
  fireProcs(ctx, ectx.sourceId, 'cast', ectx.targetId, ectx.abilityId, 0);
  runEffects(ctx, effects, stripCast(ectx));
  fireProcs(ctx, ectx.sourceId, 'castResolved', ectx.targetId, ectx.abilityId, 0, { castStartedAt, castStartSeq });
  queueEchoes(ctx, ectx); // +rogue-specs: Silhouettes repeat the cast a beat later (combat/clones.ts)
}

function stripCast(ectx: EffectCtx): EffectCtx {
  const { isCast: _drop, castStartedAt: _start, castStartSeq: _seq, ...rest } = ectx;
  return rest;
}

/** Internal interpreter. `scale` multiplies magnitudes (stacks, partial ticks). */
export function runEffects(ctx: SimContext, effects: readonly EffectSpec[], ectx: EffectCtx, scale = 1): void {
  if ((ectx.depth ?? 0) > MAX_EFFECT_DEPTH) return;
  for (const e of effects) {
    switch (e.type) {
      case 'telegraph':
        createTelegraph(ctx, e, ectx);
        break;
      case 'zone':
        createZone(ctx, e, ectx);
        break;
      case 'summon':
        summon(ctx, e, ectx);
        break;
      case 'dash':
        dash(ctx, e, ectx);
        break;
      case 'modifyCooldown':
        ctx.sys.casting.modifyCooldown(ctx, ectx.sourceId, e.abilityId, { deltaMs: e.deltaMs, reset: e.reset, addCharge: e.addCharge });
        break;
      case 'companionCast': // +ranger-specs (S7 companions): the caster's beast acts now
        if (!e.if || evalCondition(ctx, e.if, ectx.sourceId, ectx.targetId)) companionCast(ctx, e, ectx);
        break;
      case 'mechanic': // +encounters: boss/enemy mechanics (sim/src/ai/mechanics)
        if (!e.if || evalCondition(ctx, e.if, ectx.sourceId, ectx.targetId)) ctx.sys.mechanics?.apply(ctx, e, ectx);
        break;
      case 'conditional': {
        if (e.if && !evalCondition(ctx, e.if, ectx.sourceId, ectx.targetId)) break;
        const branch = evalCondition(ctx, e.cond, ectx.sourceId, ectx.targetId) ? e.then : e.else;
        if (branch) runEffects(ctx, branch, ectx, scale);
        break;
      }
      default: {
        const targets = resolveTargets(ctx, e, ectx);
        for (const tid of targets) {
          if (e.if && !evalCondition(ctx, e.if, ectx.sourceId, tid)) continue;
          applyToTarget(ctx, e, tid, ectx, scale, targets.length);
        }
      }
    }
  }
}

export function resolveTargets(ctx: SimContext, e: PerTargetEffect, ectx: EffectCtx): UnitId[] {
  if (e.to === 'self') return [ectx.sourceId];
  if (e.to === 'companion') {
    // +ranger-specs (S7 companions): the caster's living companion, if any.
    const c = companionOf(ctx, ectx.sourceId);
    return c ? [c.id] : [];
  }
  if (e.to === 'area') {
    if (!e.area) return [];
    const w = ctx.world;
    const src = unitOf(ctx, ectx.sourceId);
    const placed = ctx.sys.spatial.resolveArea(w, e.area.shape, e.area.anchor, ectx.sourceId, ectx.targetId, ectx.point);
    let ids = ctx.sys.spatial.unitsInShape(w, placed, { affects: e.area.affects, relativeTo: ectx.sourceId });
    const primary = unitOf(ctx, ectx.targetId);
    if (e.area.excludePrimary) {
      if (primary) ids = ids.filter((id) => id !== primary.id);
    } else if ((e.area.includePrimary ?? e.area.anchor === 'target') && primary?.alive && affectsMatch(src, primary, e.area.affects)) {
      ids = [primary.id, ...ids.filter((id) => id !== primary.id)];
    }
    // +ranger-specs: lowest health fraction first (Overgrowth Surge, Thorn Whip's bud).
    if (e.area.lowestHp) {
      const frac = (id: UnitId): number => {
        const u = w.units[id];
        return u && u.maxHp > 0 ? u.hp / u.maxHp : 1;
      };
      ids = ids.map((id, i) => ({ id, i })).sort((a, b) => frac(a.id) - frac(b.id) || a.i - b.i).map((x) => x.id);
    }
    if (e.type === 'heal' && e.smart !== undefined) {
      const frac = (id: UnitId): number => {
        const u = w.units[id];
        return u && u.maxHp > 0 ? u.hp / u.maxHp : 1;
      };
      ids = ids.map((id, i) => ({ id, i })).sort((a, b) => frac(a.id) - frac(b.id) || a.i - b.i).map((x) => x.id).slice(0, e.smart);
    }
    if (e.area.maxTargets !== undefined) ids = ids.slice(0, e.area.maxTargets);
    // +mage-specs (S11): one unit per `per` points spent (Skybreak's chain jumps).
    if (e.area.perSpentTargets) ids = ids.slice(0, Math.max(0, Math.floor((ectx.spent ?? 0) / Math.max(1, e.area.perSpentTargets.per)) + (e.area.perSpentTargets.bonus ?? 0)));
    return ids;
  }
  const tid = ectx.targetId ?? (selfFallback(e) ? ectx.sourceId : null);
  return tid ? [tid] : [];
}

function applyToTarget(ctx: SimContext, e: PerTargetEffect, tid: UnitId, ectx: EffectCtx, scale: number, targetCount: number): void {
  const src = ectx.sourceId;
  const depth = ectx.depth ?? 0;
  const snap = ectx.snapshot;
  switch (e.type) {
    case 'damage': {
      const r = computeAmountEx(ctx, e.amount, src, tid, snap?.power, ectx.spent); // +mage-specs (S11): spent
      let amount = r.amount * scale;
      if (e.split) amount /= Math.max(1, targetCount);
      if (snap) amount *= snap.damageDoneMult;
      dealDamage(
        ctx,
        {
          sourceId: src,
          targetId: tid,
          abilityId: ectx.abilityId,
          school: e.school,
          amount,
          preScaled: !!snap,
          critBonus: e.critBonus,
          avoidable: e.avoidable ?? (e.school === 'physical' && e.to !== 'area' && !ectx.periodic),
          isAoE: e.isAoE ?? e.to === 'area',
          periodic: ectx.periodic,
          threatMult: e.threatMult,
          stacks: r.stacks,
          ...(e.selfHarm ? { selfHarm: true } : {}), // +specs
        },
        { critChance: snap?.crit, depth, auraIid: ectx.auraIid },
      );
      return;
    }
    case 'heal': {
      const r = computeAmountEx(ctx, e.amount, src, tid, snap?.power, ectx.spent); // +mage-specs (S11): spent
      let amount = r.amount * scale;
      if (snap) amount *= snap.healingDoneMult;
      heal(ctx, { sourceId: src, targetId: tid, abilityId: ectx.abilityId, amount, preScaled: !!snap, critBonus: e.critBonus, periodic: ectx.periodic, stacks: r.stacks }, { critChance: snap?.crit, depth });
      return;
    }
    case 'absorb':
      absorb(ctx, e, tid, ectx, scale);
      return;
    case 'applyAura': {
      const def = ctx.content.aura(e.aura.id) ?? e.aura;
      applyAuraDef(ctx, def, tid, src, ectx.abilityId, { stacks: e.stacks, depth, ...(e.durationMs !== undefined ? { durationMs: e.durationMs } : {}) }); // +encounters: durationMs override
      return;
    }
    case 'removeAura': {
      const t = unitOf(ctx, tid);
      if (!t) return;
      const casterId = (e.fromSelf ?? true) ? src : undefined;
      if (e.stacks === undefined) {
        removeAura(ctx, tid, { auraId: e.auraId, casterId }, 'consumed');
        return;
      }
      for (const inst of t.auras.filter((a) => a.auraId === e.auraId && (casterId === undefined || a.casterId === casterId))) {
        removeStacks(ctx, t, inst, e.stacks, 'consumed');
      }
      return;
    }
    case 'interrupt': {
      const t = unitOf(ctx, tid);
      // Only real casts (cast time > 0) and channels can be interrupted.
      if (!t?.alive || !t.cast || (t.cast.kind === 'cast' && t.cast.durationMs <= 0)) return;
      const s = unitOf(ctx, src);
      if (s && isHostile(s, t) && t.threat) {
        addThreat(ctx, t.id, src, 0);
        ctx.sys.ai.onAttacked(ctx, t.id, src);
      }
      // +lead (F6): a successful interrupt fires the source's 'interruptDealt' procs.
      if (ctx.sys.casting.interrupt(ctx, tid, src, ectx.abilityId, e.lockoutMs)) fireProcs(ctx, src, 'interruptDealt', tid, ectx.abilityId, depth);
      return;
    }
    case 'dispel':
      dispel(ctx, e, tid, ectx);
      return;
    case 'taunt':
      taunt(ctx, tid, src, e.durationMs);
      return;
    case 'threat':
      if (e.amount !== undefined) addThreat(ctx, tid, src, e.amount * scale);
      if (e.multiply !== undefined) scaleThreat(ctx, tid, src, e.multiply);
      return;
    case 'resource': {
      const delta = changeResource(ctx, tid, e.resource, e.amount * scale, src, ectx.abilityId);
      if (delta > 0) addHealingThreat(ctx, src, tid, delta);
      return;
    }
    case 'knockback':
      knockback(ctx, e, tid, ectx);
      return;
    case 'grip':
      grip(ctx, e, tid, ectx);
      return;
    case 'setHp': {
      const t = unitOf(ctx, tid);
      if (t?.alive) t.hp = Math.max(0, Math.min(t.maxHp, Math.round(t.maxHp * e.pctMaxHp)));
      return;
    }
  }
}

function absorb(ctx: SimContext, e: AbsorbEffect, tid: UnitId, ectx: EffectCtx, scale: number): void {
  const def = ctx.content.aura(e.auraId);
  if (!def) return;
  const s = unitOf(ctx, ectx.sourceId);
  let amount = computeAmount(ctx, e.amount, ectx.sourceId, tid, ectx.snapshot?.power, ectx.spent) * scale;
  if (s) amount *= healingDoneMult(ctx, s, ectx.abilityId) * (1 + unitEffectiveStats(ctx, s).versatility);
  applyAuraDef(ctx, def, tid, ectx.sourceId, ectx.abilityId, { absorb: Math.round(amount), depth: ectx.depth ?? 0 });
}

/** Dispel (harmful: debuffs off an ally) or purge (helpful buffs off an enemy). Oldest first. */
function dispel(ctx: SimContext, e: DispelEffect, tid: UnitId, ectx: EffectCtx): void {
  const t = unitOf(ctx, tid);
  const s = unitOf(ctx, ectx.sourceId);
  if (!t?.alive) return;
  if (s && (e.harmful ? isHostile(s, t) : !isHostile(s, t))) return;
  let left = e.count ?? 1;
  for (const inst of [...t.auras]) {
    if (left <= 0) break;
    const def = ctx.content.aura(inst.auraId);
    if (!def?.dispelType || !e.dispelTypes.includes(def.dispelType)) continue;
    if (def.kind !== (e.harmful ? 'debuff' : 'buff')) continue;
    ctx.emit({ type: 'dispel', sourceID: ectx.sourceId, targetID: tid, abilityGameID: ectx.abilityId, extraAbilityGameID: inst.auraId, isBuff: def.kind === 'buff' });
    removeInstance(ctx, t, inst, 'dispelled');
    left -= 1;
  }
}

/** Forced displacement; cancels the victim's cast. Bosses (ccImmune) are unaffected. */
function displace(ctx: SimContext, t: Unit, to: Vec2, durationMs: number): void {
  const dest = ctx.sys.spatial.clampToWalkable(ctx.world, to);
  const dur = Math.max(TICK_MS, Math.round(durationMs));
  const dist = Math.hypot(dest.x - t.pos.x, dest.y - t.pos.y);
  t.move = { mode: 'forced', to: dest, speed: dist / (dur / 1000), until: ctx.now + dur };
  if (t.cast) ctx.sys.casting.cancelCast(ctx, t.id, 'cc');
}

function knockback(ctx: SimContext, e: KnockbackEffect, tid: UnitId, ectx: EffectCtx): void {
  const t = unitOf(ctx, tid);
  const s = unitOf(ctx, ectx.sourceId);
  if (!t?.alive || !s || t.ccImmune || t.id === s.id) return;
  const dir = dirBetween(s.pos, t.pos, s.facing);
  displace(ctx, t, { x: t.pos.x + dir.x * e.distance, y: t.pos.y + dir.y * e.distance }, e.durationMs);
}

function grip(ctx: SimContext, e: GripEffect, tid: UnitId, ectx: EffectCtx): void {
  const t = unitOf(ctx, tid);
  const s = unitOf(ctx, ectx.sourceId);
  if (!t?.alive || !s || t.ccImmune || t.id === s.id) return;
  const dir = dirBetween(s.pos, t.pos, s.facing);
  const to = { x: s.pos.x + dir.x * e.stopAt, y: s.pos.y + dir.y * e.stopAt };
  displace(ctx, t, to, (Math.hypot(to.x - t.pos.x, to.y - t.pos.y) / GRIP_SPEED) * 1000);
}

function dash(ctx: SimContext, e: DashEffect, ectx: EffectCtx): void {
  const s = unitOf(ctx, ectx.sourceId);
  if (!s?.alive) return;
  let dir: Vec2;
  let travel: number;
  if (e.mode === 'toTarget') {
    const t = unitOf(ctx, ectx.targetId);
    if (!t) return;
    dir = dirBetween(s.pos, t.pos, s.facing);
    const reach = Math.max(MIN_MELEE_RANGE, s.combatReach + t.combatReach + MELEE_RANGE_BONUS) - 0.5;
    travel = Math.max(0, Math.hypot(t.pos.x - s.pos.x, t.pos.y - s.pos.y) - reach);
  } else if (e.mode === 'behindTarget') {
    // +classes: to the target's back, facing it.
    const t = unitOf(ctx, ectx.targetId);
    if (!t) return;
    const back = { x: t.pos.x - Math.cos(t.facing) * (t.radius + s.radius + 0.8), y: t.pos.y - Math.sin(t.facing) * (t.radius + s.radius + 0.8) };
    const to = ctx.sys.spatial.clampToWalkable(ctx.world, back);
    if (e.distance !== undefined && Math.hypot(to.x - s.pos.x, to.y - s.pos.y) > e.distance) return;
    s.pos = to;
    s.move = null;
    s.facing = Math.atan2(t.pos.y - to.y, t.pos.x - to.x);
    return;
  } else if (e.mode === 'backward') {
    // +classes: a leap back; the hero keeps looking where it was.
    const keep = s.facing;
    const travelBack = e.distance ?? 0;
    const to = ctx.sys.spatial.clampToWalkable(ctx.world, { x: s.pos.x - Math.cos(keep) * travelBack, y: s.pos.y - Math.sin(keep) * travelBack });
    const d = Math.hypot(to.x - s.pos.x, to.y - s.pos.y);
    if (!e.speed) {
      s.pos = to;
      s.move = null;
    } else s.move = { mode: 'forced', to, speed: e.speed, until: ctx.now + Math.max(TICK_MS, Math.round((d / e.speed) * 1000)) };
    s.facing = keep;
    return;
  } else if (e.mode === 'toPoint') {
    if (!ectx.point) return;
    dir = dirBetween(s.pos, ectx.point, s.facing);
    travel = Math.hypot(ectx.point.x - s.pos.x, ectx.point.y - s.pos.y);
  } else {
    dir = { x: Math.cos(s.facing), y: Math.sin(s.facing) };
    travel = e.distance ?? 0;
  }
  if (e.distance !== undefined) travel = Math.min(travel, e.distance);
  const dest = ctx.sys.spatial.clampToWalkable(ctx.world, { x: s.pos.x + dir.x * travel, y: s.pos.y + dir.y * travel });
  s.facing = Math.atan2(dir.y, dir.x);
  if (!e.speed) {
    s.pos = dest;
    s.move = null;
    return;
  }
  s.move = { mode: 'forced', to: dest, speed: e.speed, until: ctx.now + Math.max(TICK_MS, Math.round((travel / e.speed) * 1000)) };
}

function anchorPos(ctx: SimContext, anchor: SummonEffect['anchor'], ectx: EffectCtx, s: Unit): Vec2 {
  if (anchor === 'point' && ectx.point) return ectx.point;
  if (anchor === 'target') {
    const t = unitOf(ctx, ectx.targetId);
    if (t) return t.pos;
  }
  return s.pos;
}

function summon(ctx: SimContext, e: SummonEffect, ectx: EffectCtx): void {
  const s = unitOf(ctx, ectx.sourceId);
  if (!s?.alive) return;
  const c = anchorPos(ctx, e.anchor, ectx, s);
  const center = { x: c.x, y: c.y };
  if (e.pet) return spawnPets(ctx, e, s, center, ectx.abilityId); // +acolyte-specs (S7): a friendly pet
  if (e.companion) return spawnCompanion(ctx, e, s, center, ectx.abilityId); // +ranger-specs (S7 companions): the spirit beast
  for (let i = 0; i < e.count; i++) {
    const ang = s.facing + (2 * Math.PI * i) / Math.max(1, e.count);
    const pos = ctx.sys.spatial.clampToWalkable(ctx.world, { x: center.x + Math.cos(ang) * e.radius, y: center.y + Math.sin(ang) * e.radius });
    const n = ctx.ops.spawnNpc({ npcId: e.npcId, pos, facing: s.facing, packId: s.packId, summonerId: s.id });
    ctx.emit({ type: 'summon', sourceID: s.id, targetID: n.id, abilityGameID: ectx.abilityId });
    if ((e.joinCombat ?? true) && s.inCombat && s.threat) {
      // The summoner's victim goes on the table first so it becomes the add's victim.
      const order = [s.threat.victimId, ...ctx.world.unitOrder].filter((id): id is UnitId => !!id && !!s.threat!.entries[id]);
      for (const pid of order) addThreat(ctx, n.id, pid, 0);
      if (n.threat?.victimId) ctx.sys.ai.onAttacked(ctx, n.id, n.threat.victimId);
    }
  }
}

function createTelegraph(ctx: SimContext, e: TelegraphEffect, ectx: EffectCtx): void {
  if (!unitOf(ctx, ectx.sourceId)) return;
  const area = ctx.sys.spatial.resolveArea(ctx.world, e.shape, e.anchor, ectx.sourceId, ectx.targetId, ectx.point);
  const followUnitId = e.follow ? (e.anchor === 'caster' ? ectx.sourceId : e.anchor === 'target' ? ectx.targetId : null) : null;
  const id = ctx.newId('t');
  const resolvesAt = ctx.now + e.delayMs;
  ctx.world.telegraphs.push({
    id,
    sourceId: ectx.sourceId,
    abilityId: ectx.abilityId,
    area,
    affects: e.affects,
    createdAt: ctx.now,
    resolvesAt,
    followUnitId,
    effects: e.effects,
    repeatsLeft: e.repeat?.repeats ?? 0,
    repeatIntervalMs: e.repeat?.intervalMs ?? 0,
  });
  ctx.emit({ type: 'telegraph_start', telegraphId: id, sourceID: ectx.sourceId, abilityGameID: ectx.abilityId, area, resolvesAt });
}

function createZone(ctx: SimContext, e: ZoneEffect, ectx: EffectCtx): void {
  const s = unitOf(ctx, ectx.sourceId);
  if (!s) return;
  if (e.uniquePerCaster ?? true) {
    for (const z of ctx.world.zones.filter((z) => z.defId === e.zoneId && z.sourceId === s.id)) {
      if (z.insideAuraId) for (const uid of z.inside) removeAura(ctx, uid, { auraId: z.insideAuraId, casterId: z.sourceId }, 'replaced');
    }
    ctx.world.zones = ctx.world.zones.filter((z) => !(z.defId === e.zoneId && z.sourceId === s.id));
  }
  const area = ctx.sys.spatial.resolveArea(ctx.world, e.shape, e.anchor, s.id, ectx.targetId, ectx.point);
  const interval = e.tick ? (e.tick.hasteScaled ? Math.max(1, Math.round(e.tick.intervalMs / (1 + unitEffectiveStats(ctx, s).haste))) : e.tick.intervalMs) : 0;
  const zone: Zone = {
    id: ctx.newId('z'),
    defId: e.zoneId,
    sourceId: s.id,
    abilityId: ectx.abilityId,
    area,
    affects: e.affects,
    createdAt: ctx.now,
    expiresAt: ctx.now + e.durationMs,
    tick: e.tick ? { intervalMs: interval, effects: e.tick.effects } : null,
    nextTickAt: e.tick ? ctx.now + interval : null,
    insideAuraId: e.insideAura?.id ?? null,
    inside: [],
  };
  if (e.snapshot) zone.snapshot = zoneSnapshot(ctx, s, ectx.abilityId);
  if (e.triggerOnce) zone.triggerOnce = true; // +basekits
  if (e.onExpire?.length) zone.onExpire = e.onExpire;
  if (e.motion?.type === 'linear') {
    // +basekits: a travelling area (moved each tick by the mechanics hazard step).
    let h = s.facing;
    if (e.motion.heading === 'target') {
      const t = unitOf(ctx, ectx.targetId ?? s.targetId);
      const to = t && t.id !== s.id && t.alive ? t.pos : ectx.point;
      if (to && (to.x !== area.origin.x || to.y !== area.origin.y)) h = Math.atan2(to.y - area.origin.y, to.x - area.origin.x);
    }
    zone.area = { ...zone.area, facing: h };
    zone.motion = { spec: e.motion, vel: { x: Math.cos(h) * e.motion.speed, y: Math.sin(h) * e.motion.speed } };
  }
  ctx.world.zones.push(zone);
}

/**
 * +lead: rotation rework (F9). Caster stats for a snapshotting zone, including mods scoped to the
 * zone's ability (and its school): the ticks then deal what the cast would have dealt.
 */
export function zoneSnapshot(ctx: SimContext, s: Unit, abilityId: string): NonNullable<Zone['snapshot']> {
  const st = unitEffectiveStats(ctx, s);
  const school = ctx.content.ability(abilityId)?.school ?? 'physical';
  return {
    power: st.power,
    crit: critChanceFor(ctx, s, abilityId),
    damageDoneMult: damageDoneMult(ctx, s, school, abilityId) * (1 + st.versatility) * s.scaling.damage,
    healingDoneMult: healingDoneMult(ctx, s, abilityId) * (1 + st.versatility),
  };
}
